import { Archive, CheckCircle, RocketLaunch, Warning } from '@/core/ui/icons';
import { useState } from 'react';
import { errorMessage } from '@/core/http';
import { Button, Card, Dialog } from '@/core/ui';
import { useArchiveCourse, usePublishCourse } from '../application/queries';
import { CourseStatus, publishBlockers, type Course } from '../domain/course';

/**
 * Ciclo de vida do curso: o que falta para publicar, publicar e arquivar.
 * As pendências espelham `Course.publish()` — a API continua decidindo.
 */
export function PublishPanel({ course }: { course: Course }) {
  const publish = usePublishCourse(course.id);
  const archive = useArchiveCourse(course.id);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const blockers = publishBlockers(course);
  const error = publish.error ?? archive.error;

  return (
    <Card className="p-5">
      <h2 className="text-base font-semibold">Publicação</h2>

      {course.status === CourseStatus.DRAFT && (
        <>
          {blockers.length === 0 ? (
            <p className="mt-3 flex items-start gap-2 text-sm text-brand-700">
              <CheckCircle aria-hidden className="mt-0.5 size-4 shrink-0" />
              Tudo pronto. Ao publicar, o curso aparece na vitrine de aulas.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {blockers.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-fg-muted">
                  <Warning aria-hidden className="mt-0.5 size-4 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          )}
          <Button
            className="mt-4 w-full"
            disabled={blockers.length > 0}
            loading={publish.isPending}
            onClick={() => publish.mutate()}
          >
            <RocketLaunch aria-hidden className="size-4" />
            Publicar curso
          </Button>
        </>
      )}

      {course.status === CourseStatus.PUBLISHED && (
        <>
          <p className="mt-3 text-sm text-fg-muted">Publicado e visível no portal.</p>
          <Button variant="danger" className="mt-4 w-full" onClick={() => setConfirmArchive(true)}>
            <Archive aria-hidden className="size-4" />
            Arquivar curso
          </Button>
        </>
      )}

      {course.status === CourseStatus.ARCHIVED && (
        <p className="mt-3 text-sm text-fg-muted">Arquivado. Não aparece na vitrine nem aceita matrículas.</p>
      )}

      {error && <p className="mt-3 text-sm text-fg">{errorMessage(error)}</p>}

      <Dialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        title="Arquivar curso?"
        description={course.title}
      >
        <p className="text-sm text-fg-muted">
          O curso sai do portal. Quem já começou continua com o histórico.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmArchive(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={archive.isPending}
            onClick={() => archive.mutate(undefined, { onSuccess: () => setConfirmArchive(false) })}
          >
            Arquivar
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
